import * as React from 'react';
import glamorous from 'glamorous';
import gql from 'graphql-tag';
import { RvLoader } from '../../components/Loader';
import { Query } from 'react-apollo';
import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { formatNumber, getLabels, getMaxTickLimit, getSuggestedMaxTick } from '../../helpers/ChartHelper';
import { ChartCommonProps, IconButtonWrapper, ChartCommonState, HelpWrapper } from '../Common';
import { WithStyles, withStyles, IconButton } from '@material-ui/core';
import { styles } from '../styles';
import { ChartFilter } from '../ChartComponents';
import { SelectedItemProps } from '../MattersByStatusChart';
import { DrillDownType } from '../DrillDownType';

import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick, Chart, ChartEvent, ActiveElement } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { ChartInfo } from '../../components/ChartInfo';

import { RV_CHART_CLASS, RV_CHART_COLORS, isChartDataEmpty, rvBarDatasetStyle, rvChartAnimation, rvCategoryAxisStyle, rvLegendLabels, rvTooltipStyle, rvValueAxisStyle } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { ChartEmptyState } from '../ChartEmptyState';
import FolderOpenOutlinedIcon from '@material-ui/icons/FolderOpenOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);

const BarWrapper = glamorous.div<{ theme?: object }>((props) => ({
    position: 'relative',
    paddingBottom: '10px',
    flex: 1,
    display: 'flex',
    flexFlow: 'column',
    maxWidth: '100%',
    maxHeight: '100%',
    height: '100%',
    '@media print': {
        display: 'flex',
        flexFlow: 'column',
        alignItems: 'center',
    },
}));

const BarTitleContainer = glamorous.div<{ theme?: object }>((props) => ({
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
    padding: '15px 15px 0px 15px',
    fontSize: '16px',
    fontWeight: 600,
    [`& .${`h1`}`]: {},
    [`& .${`h2`}`]: {
        fontSize: '2.5rem',
        padding: '10px 0',
    },
    [`& .${`h3`}`]: {
        paddingTop: '10px',
    },
})).withProps({ className: 'rv-chart-title-area' });

const BarContainer = glamorous.div<{ theme?: object }>((props) => ({
    padding: '5px 15px 15px 15px',
    minHeight: '300px',
    flex: 1,
    display: 'flex',
    flexFlow: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    maxHeight: '350px',
    maxWidth: 'auto',
    position: 'relative',
    '@media (max-width: 533px)': {
        maxWidth: '350px',
        margin: '0 auto',
    },
    [`& .${`titleWrapper`}`]: {
        textAlign: 'center',
        display: 'inline-block',
        width: '100%',
        padding: '15px',
        [`& .${`title`}`]: {
            fontSize: '18px',
            fontWeight: 600,
            display: 'block',
            marginBottom: '15px',
        },
        [`& .${`value`}`]: {
            fontSize: '24px',
            fontWeight: 600,
            display: 'block',
            marginBottom: '15px',
        },
        [`& .${`secondary-title`}`]: {
            fontSize: '16px',
            fontWeight: 400,
            display: 'block',
            marginBottom: '15px',
        },
    },
}));

const LoadWrapper = glamorous.div<{ theme?: object }>((props) => ({
    padding: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
}));

const TotalDiv = glamorous.div<{ theme?: object }>((props) => ({
    cursor: 'pointer',
    paddingRight: 5,
}));

interface ChartProps extends SelectedItemProps, ChartCommonProps, WithStyles<typeof styles> {
    chartFilter?: ChartFilter;
}

interface ChartState extends ChartCommonState {
    //
}

class ClosedMattersChart extends React.Component<ChartProps, ChartState> {
    // tslint:disable-next-line: no-any
    public chartRef: React.RefObject<any>;

    constructor(props: ChartProps, state: ChartState) {
        super(props, state);
        this.onCloseChart = this.onCloseChart.bind(this);
        this.handlePopoverOpen = this.handlePopoverOpen.bind(this);
        this.handlePopoverClose = this.handlePopoverClose.bind(this);
        this.onHover = this.onHover.bind(this);

        this.onPan = this.onPan.bind(this);

        this.chartRef = React.createRef();

        this.state = {
            open: false,
            anchorEl: undefined,
        };
    }

    public componentDidMount() {
        //
    }

    public componentWillUnmount() {
        //
    }

    public shouldComponentUpdate(nextProps: ChartProps, nextState: ChartState): boolean {
        if (this.props !== nextProps || this.state !== nextState) {
            return true;
        }

        return false;
    }

    public render() {
        // const { classes } = this.props;

        return (
            <React.Fragment>
                {/* tslint:disable-next-line: no-any */}
                <Query<any>
                    query={MattersClosedInLastTwelveMonthsData}
                    variables={{
                        entityType: this.props.chartFilter?.entityType,
                        entityGuid: this.props.chartFilter?.entityGuid,
                        responsibility: this.props.chartFilter?.responsibility,
                    }}
                >
                    {({ loading, error, data }) => {
                        if (loading) {
                            return (
                                <ChartSkeleton variant="columns" />
                            );
                        }

                        const statistics = data.statistics;

                        let matter = loading || error || statistics === null || statistics === undefined ? null : statistics.matter;

                        let mattersClosedInLastTwelveMonth = matter
                            ? matter.mattersClosedInLastTwelveMonths
                                ? matter.mattersClosedInLastTwelveMonths.mattersClosedInLastTwelveMonth
                                : null
                            : null;

                        if (mattersClosedInLastTwelveMonth) {
                            // tslint:disable-next-line: no-any
                            let months = mattersClosedInLastTwelveMonth.map((value: any) => {
                                return value.month;
                            });

                            let highestValue = 0;

                            // tslint:disable-next-line: no-any
                            let matterCount = mattersClosedInLastTwelveMonth.map((value: any) => {
                                if (value.count > highestValue) {
                                    highestValue = value.count;
                                }
                                return value.count;
                            });
                            let totalCount = matter.mattersClosedInLastTwelveMonths.totalCount;

                            return (
                                <BarWrapper className={RV_CHART_CLASS}>
                                    <IconButtonWrapper aria-label="close">
                                        <HelpWrapper className="rv-chart-help">
                                            <ChartInfo 
                                                description={this.props.description}
                                            />
                                        </HelpWrapper>
                                        <IconButton aria-label="close" onClick={this.onCloseChart} size="small">
                                            <CloseIcon />
                                        </IconButton>
                                    </IconButtonWrapper>
                                    <BarTitleContainer>
                                        <ChartHeading icon={<FolderOpenOutlinedIcon />} title="Closed Matters in the last 12 months" />
                                        <TotalDiv className="h2" onClick={() => this.handleTotalClick()}>
                                            <AnimatedNumber value={totalCount} format={(amount) => `${formatNumber(amount)}`} />
                                        </TotalDiv>
                                    </BarTitleContainer>
                                    <BarContainer className="rv-chart-bars">
                                        <Bar
                                            ref={this.chartRef}
                                            // tslint:disable-next-line: no-any
                                            onClick={(event: React.MouseEvent<any>) => {
                                                const _data = getElementAtEvent(this.chartRef.current, event);
                                                if (_data.length > 0 && this.props.getSelectedItem) {
                                                    const _index = getElementAtEvent(this.chartRef.current, event)[0].index;

                                                    const item = months[_index];

                                                    const searchCriterias = new Array<SearchCriteria>();

                                                    searchCriterias.push(
                                                        { property: 'CompletionDateMonthStart', operator: 'gteq', comparison: item },
                                                        { property: 'CompletionDateMonthEnd', operator: 'lteq', comparison: item },
                                                        { property: 'InProgress', operator: 'eq', comparison: '0' },
                                                    );

                                                    this.props.getSelectedItem(
                                                        searchCriterias,
                                                        true,
                                                        'Closed Matters in the last 12 months',
                                                        DrillDownType.matterSummaryDrilldown,
                                                        'Matters',
                                                    );
                                                }
                                            }}
                                            data={{
                                                labels: getLabels(months),
                                                datasets: [
                                                    {
                                                        label: 'Closed Cases',
                                                        ...rvBarDatasetStyle(RV_CHART_COLORS.primary),
                                                        data: matterCount,
                                                    },
                                                ],
                                            }}
                                            options={{
                                                onHover: this.onHover,
                                                indexAxis: 'x' as const,
                                                animation: rvChartAnimation(),
                                                elements: { bar: { borderWidth: 0 } },
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                scales: {
                                                    y: {
                                                        grid: rvValueAxisStyle.grid,
                                                        // beginAtZero: true,
                                                        suggestedMax: getSuggestedMaxTick(matterCount),
                                                        beginAtZero: true,
                                                        ticks: {
                                                            ...rvValueAxisStyle.ticks,
                                                            maxTicksLimit: getMaxTickLimit(matterCount),
                                                            autoSkip: false,
                                                            callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                                                return `${formatNumber(tickValue)}`;
                                                            },
                                                        },
                                                    },
                                                    x: {
                                                        grid: rvCategoryAxisStyle.grid,
                                                        // min: 0,
                                                        // max: 9 // minimum display
                                                        ticks: {
                                                            ...rvCategoryAxisStyle.ticks,
                                                            autoSkip: false
                                                        }
                                                    }
                                                },
                                                plugins: {
                                                    legend: {
                                                        position: 'bottom' as const,
                                                        display: true,
                                                        labels: rvLegendLabels,
                                                    },
                                                    title: {
                                                        display: false,
                                                        text: ``,
                                                    },
                                                    zoom: {
                                                        pan: {
                                                            enabled: false,
                                                            onPan: this.onPan,
                                                        },
                                                        zoom: {
                                                            wheel: {
                                                                enabled: false,
                                                            },
                                                        },
                                                        // limits: {
                                                        //   y: { min: 0, max: 9 },
                                                        // },
                                                    },
                                                    tooltip: {
                                                        ...rvTooltipStyle,
                                                        callbacks: {
                                                            // tslint:disable-next-line: no-any
                                                            label: function (context: any) {
                                                                const {
                                                                    dataIndex,
                                                                    //   label,
                                                                    //   formattedValue,
                                                                    dataset,
                                                                } = context;

                                                                var currentValue = matterCount[dataIndex];

                                                                return `${dataset.label}: ${formatNumber(currentValue)}`;
                                                            },
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                        {isChartDataEmpty(matterCount) && (
                                            <ChartEmptyState title="No data" hint="Nothing to show for this selection." />
                                        )}
                                    </BarContainer>
                                </BarWrapper>
                            );
                        }

                        return (
                            <LoadWrapper className="load-wrapper">
                                <RvLoader size="small" />
                            </LoadWrapper>
                        );
                    }}
                </Query>
            </React.Fragment>
        );
    }

    private handleTotalClick() {
        if (this.props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();

            searchCriterias.push({ property: '12MonthPeriod', operator: 'eq', comparison: 'closedMatters' }, { property: 'InProgress', operator: 'eq', comparison: '0' });

            this.props.getSelectedItem(searchCriterias, true, 'Closed Matters in the last 12 months', DrillDownType.matterSummaryDrilldown, 'Matters');
        }
    }

    private onCloseChart() {
        if (this.props.onCloseChart) {
            return this.props.onCloseChart('isClosedMattersChartOpen');
        }
    }

    // tslint:disable-next-line: no-any
    private handlePopoverOpen(event: any) {
        this.setAnchorEl(event.currentTarget);
    }

    private handlePopoverClose = () => {
        this.setAnchorEl(null);
    }

    // tslint:disable-next-line: no-any
    private setAnchorEl(anchorElValue: any) {
        this.setState({
            anchorEl: anchorElValue,
            open: Boolean(anchorElValue),
        });
    }

    private onPan = (context: { chart: Chart }) => {
        this.chartRef.current.options.scales.y.min = 0;
        this.chartRef.current.update();
    }

    private onHover(event: ChartEvent, elements: ActiveElement[], chart: Chart) {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    }
}

// tslint:disable-next-line: no-any
export default withStyles(styles as any, { withTheme: true })(ClosedMattersChart as any) as any;

const MattersClosedInLastTwelveMonthsData = gql`
    query MattersClosedInLastTwelveMonthsData($entityType: Int, $entityGuid: String, $responsibility: Int) {
        statistics {
            matter {
                mattersClosedInLastTwelveMonths(entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility) {
                    totalCount
                    mattersClosedInLastTwelveMonth {
                        month
                        count
                    }
                }
            }
        }
    }
`;
